import angular from "angular";

import JSZip from "@progress/jszip-esm";

import { saveAs } from "file-saver";

import { ApiListItem, Calculation, CalcWorker, Dates, HouseholdYear, HouseholdYears, HouseholdYearProcessingStatus, ODataResult, ReportType, Texts, Years, YearlySideCosts, TaxDeductionWorkCategories, HouseholdYearListItem } from "@salaxy/core";

import { ReportsService, SessionService, UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";

/**
 * Provides CRUD access to household yearly deductions.
 */
export class HouseholdYearCrudController extends ApiCrudObjectController<HouseholdYear, HouseholdYearListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "HouseholdYears",
    "UiHelpers",
    "$location",
    "$routeParams",
    "ReportsService",
    "SessionService",
  ];

  private _yearlyCalculations: {
     year: number,
     calculations: Calculation[],
     modifiedCalculations: Calculation[]
     status: "loading" | "loaded",
     workers: Array<{
       worker: CalcWorker,
       mandatorySideCosts: number,
       totalTaxable: number,
       calculations: Calculation[],
      }>
  } = { year: null, calculations: [], modifiedCalculations: [], status: "loaded", workers: [] };

  constructor(
    private householdYearsApi: HouseholdYears,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private reportsService: ReportsService,
    private sessionService: SessionService,
  ) {
    super(householdYearsApi, uiHelpers, $location, $routeParams);
  }

  /**
   * Initializes the controller
   */
  public $onInit() {
    super.$onInit();
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/household-years",
      detailsUrl: this.detailsUrl || "/household-years/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/HouseholdYears.html",
      oDataOptions: {},
    };
  }

  /** Save changes to the current item */
  public async save(): Promise<HouseholdYear> {
    for (const calc of this.yearlyCalculations.modifiedCalculations) {
      const updatedCalc = await this.householdYearsApi.updateCalculation(calc);
      const i = this.yearlyCalculations.calculations.indexOf(calc);
      if (i >= 0) {
          angular.copy(updatedCalc,this.yearlyCalculations.calculations[i]);
      }
    }
    this.yearlyCalculations.modifiedCalculations.splice(0,  this.yearlyCalculations.modifiedCalculations.length);
    return super.save();
  }

  /** Returns all paid calculations for the household year */
  public get yearlyCalculations(): {year: number, calculations: Calculation[], modifiedCalculations: Calculation[], status: "loading" | "loaded",  workers: Array<{
    worker: CalcWorker,
    mandatorySideCosts: number,
    totalTaxable: number,
    calculations: Calculation[],
   }>} {
    if (!this.current || !this.current.year) {
      this._yearlyCalculations.year = null;
      this._yearlyCalculations.status = "loaded";
      this._yearlyCalculations.calculations.splice(0,  this._yearlyCalculations.calculations.length);
      this._yearlyCalculations.modifiedCalculations.splice(0,  this._yearlyCalculations.modifiedCalculations.length);
      this._yearlyCalculations.workers.splice(0,  this._yearlyCalculations.calculations.length);
    } else {
      if (this._yearlyCalculations.year !== this.current.year &&  this._yearlyCalculations.status !== "loading") {
        this._yearlyCalculations.year = this.current.year;
        this._yearlyCalculations.status = "loading";
        this.householdYearsApi.getCalculationsForYear(this._yearlyCalculations.year).then( (calculations) => {
          this._yearlyCalculations.calculations = [];
          this._yearlyCalculations.modifiedCalculations = [];
          this._yearlyCalculations.workers = [];
          this._yearlyCalculations.calculations.push(...calculations);

          for(const calc of this._yearlyCalculations.calculations) {
            let item = this._yearlyCalculations.workers.find( (x) => x.worker.paymentData.socialSecurityNumberValid == calc.worker.paymentData.socialSecurityNumberValid);
            if (!item) {
              item = { worker: calc.worker, totalTaxable: 0, mandatorySideCosts:0, calculations: [] };
              this._yearlyCalculations.workers.push(item);
            }
            item.calculations.push(calc);
            item.mandatorySideCosts += calc.result.employerCalc.mandatorySideCosts;
            item.totalTaxable += calc.result.totals.totalTaxable;
          }
          this._yearlyCalculations.status = "loaded";
        });
      }
    }
    return this._yearlyCalculations;
  }

    /** Returns true if the feedback is active */
    public get isOpen(): boolean {
      if (!this.current) {
        return false;
      }

      return this.sessionService.isInSomeRole(["pro", "admin"]) ||
             !this.current.processingStatus || this.current.processingStatus === HouseholdYearProcessingStatus.Open;
    }


  /** Calculates the household tax deduction total */
  public getTaxDeductionTotal() {
    let deductibleTotal = 0;
    const calcs = this.yearlyCalculations.calculations;

    for (const calc of calcs) {
      if (calc.usecase?.data?.isHouseholdDeductible && !this.isHouseholdDeductionExtended(calc)) {
        deductibleTotal += calc.result.employerCalc.mandatorySideCosts;
        deductibleTotal += ( calc.result.totals.totalTaxable * this.getHouseholdDeduction(calc) );
      }
    }
    return deductibleTotal;
  }

   /** Calculates the household tax deduction total */
   public getTaxDeductionTotalExtended() {
    let deductibleTotal = 0;
    const calcs = this.yearlyCalculations.calculations;

    for (const calc of calcs) {
      if (calc.usecase?.data?.isHouseholdDeductible && this.isHouseholdDeductionExtended(calc)) {
        deductibleTotal += calc.result.employerCalc.mandatorySideCosts;
        deductibleTotal += ( calc.result.totals.totalTaxable * this.getHouseholdDeduction(calc) );
      }
    }
    return deductibleTotal;
  }

  /** Returns true if the deduction should be split between spouses */
  public canSplitWithSpouse() {
    return this.getSplittableMaxAmount() > 0;
  }

  /** Returns current deduction max amount */
  public getSplittableMaxAmount() {
    let maxAmount = 0;
    if ((this.getTaxDeductionTotal() + (this.current.insuranceAmount ?? 0)) > (this.getHouseholdDeductionMaxAmount() + this.getHouseholdDeductionDeductible())) {
      maxAmount = this.getHouseholdDeductionMaxAmount();
    }
    if ( (Math.min(this.getTaxDeductionTotal(), this.getHouseholdDeductionMaxAmount() + this.getHouseholdDeductionDeductible()) + this.getTaxDeductionTotalExtended() + (this.current.insuranceAmount ?? 0)) > (this.getHouseholdDeductionMaxAmountExtended() + this.getHouseholdDeductionDeductible())) {
      maxAmount = this.getHouseholdDeductionMaxAmountExtended();
    }
    return maxAmount;
  }

  /** Returns household deduction for calculation */
  public getHouseholdDeduction(calc: Calculation) {
    const category = this.getTaxDeductionWorkCategories(calc);
    const sideCosts = this.getYearlySideCosts();

    if (this.getHouseholdDeductionCategoriesExtended().includes(category)) {
      return sideCosts.householdDeductionExtended;
    }
    return sideCosts.householdDeduction;
  }

  /** Returns deduction deductible */
  public getHouseholdDeductionDeductible() {
    const sideCosts = this.getYearlySideCosts();
    return sideCosts.householdDeductionDeductible;
  }

  /**
   * Gets a link URL for a yearly report. This is a full link with token and absolute URL.
   * @param type - Type of the report must be one of the yearly reports
   * @param year - Year for the report
   * @param id - Worker ID for those reports that are specific to one Worker.
   * @param id2 - Second Worker ID for those reports that have two Workers in one report
   */
   public getYearlyReport(type: ReportType, year: number, id?: string, id2?: string) {
    return this.reportsService.getYearlyReportUrl(type, year, id, id2);
  }

  /** Set calculation modified */
  public calculationUpdated(calc: Calculation) {
    const existingCalc = this.yearlyCalculations.modifiedCalculations.find( (x) => x.id === calc.id);
    if (!existingCalc) {
      const newCalc = this.yearlyCalculations.calculations.find( (x) => x.id == calc.id);
      if (newCalc) {
        this.yearlyCalculations.modifiedCalculations.push(newCalc);
      }
    }
  }

  /**
   * Sets the active year to the controller.
   */
  public setActiveYear() {
    const now = new Date();
    const year = now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear()  - 1;

    this.householdYearsApi.getOData(null).then( (result) => {
      const odata = result as any as ODataResult<ApiListItem>;
      const logicalDate = `${year}-01-01T00:00:00Z`;
      const report = odata.items.find( (x) => x.logicalDate == logicalDate);
      if (report) {
        this.householdYearsApi.getSingle(report.id).then( (yearReport) => {
          this.model = yearReport;
        });
      }
      else
      {
        const newModel = this.api.getBlank();
        newModel.year = year;
        this.model = newModel;
      }
    });
  }

  /** Export pdf for the given deductions */
  public async export() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait", "Ladataan PDF-tiedostoja...");
    const results =  await this.downloadReportsForHousehold();
    if (results.length === 0) {
      loading.dismiss();
    } else {
      if (results.length === 1) {
        this.uiHelpers.downloadFile(results[0].blob, results[0].fileName);
        loading.dismiss();
      } else {
        loading.setText(`Muodostetaan pakattu tiedosto`);
        const zip = new JSZip();
        results.forEach(item => {
          zip.file(`${item.fileName}`, item.blob);
        });
        const blob =  await zip.generateAsync({
          type: "blob",
          mimeType:
            "application/zip",
        });
        saveAs(blob, `${Texts.escapeFileName(`14B_${Dates.format("today","YYYYMMDD")}`)}.zip`);
        loading.dismiss();
      }
    }
  }

  private async downloadReportsForHousehold(): Promise<Array<{blob: Blob, fileName: string}>> {
    if (!this.current?.id) {
      return Promise.resolve([]);
    }
    const results = [];
    const pdfs = await this.householdYearsApi.downloadPdfs(this.current.id);
    for(const pdf of pdfs) {
      results.push( {
        fileName: pdf.fileName,
        blob: this.uiHelpers.bytes2Blob(pdf.bytes,"application/pdf"),
      });
    }
    return results;
  }

   private  getYearlySideCosts(): YearlySideCosts {
    if (!this.current || !this.current.year) {
      return Years.getYearlyChangingNumbers(Dates.getToday()).sideCosts;
    }
    return Years.getYearlyChangingNumbers(`${this.current.year}-01-01`).sideCosts;
  }

  private getHouseholdDeductionMaxAmount() {
    const sideCosts = this.getYearlySideCosts();
    return sideCosts.householdDeductionMaxAmount;
  }

  private getHouseholdDeductionMaxAmountExtended() {
    const sideCosts = this.getYearlySideCosts();
    return sideCosts.householdDeductionMaxAmountExtended;
  }

  private getTaxDeductionWorkCategories(calc: Calculation) : TaxDeductionWorkCategories {
    return calc.usecase?.data?.taxDeductionCategories ?? TaxDeductionWorkCategories.None;
  }

  private getHouseholdDeductionCategoriesExtended(): TaxDeductionWorkCategories[] {
    const sideCosts = this.getYearlySideCosts();
    return sideCosts.householdDeductionCategoriesExtended ?? [];
  }

  private isHouseholdDeductionExtended(calc: Calculation): boolean {
    const category = this.getTaxDeductionWorkCategories(calc);
    return this.getHouseholdDeductionCategoriesExtended().includes(category);
  }
}
